import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Search, Download, Copy, X, ExternalLink, Send } from "lucide-react";
import AdminWwbaBalances from "@/pages/admin/AdminWwbaBalances";

const EVENT_SLUG = "wwba-florida"; // resolved to an id at runtime (env-agnostic)
const money = (c) => `$${((c || 0) / 100).toFixed(0)}`;
const POS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "OF", "UTIL"];
const REG_STATUSES = ["Invited", "Registration Started", "Deposit Pending", "Registered", "Withdrawn", "Cancelled"];
const inp = "border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white";
const DEP_TONE = { "Deposit Paid": "bg-emerald-100 text-emerald-700", "Deposit Pending": "bg-amber-100 text-amber-700", "Deposit Not Paid": "bg-slate-100 text-slate-500", "Payment Failed": "bg-rose-100 text-rose-700" };
const REG_TONE = { Registered: "bg-emerald-100 text-emerald-700", "Deposit Pending": "bg-amber-100 text-amber-700", Invited: "bg-slate-100 text-slate-500", "Registration Started": "bg-blue-100 text-blue-700", Withdrawn: "bg-slate-200 text-slate-400", Cancelled: "bg-slate-200 text-slate-400" };

export default function AdminWwba() {
  const [eventId, setEventId] = useState(null);
  const [eventErr, setEventErr] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [deposit, setDeposit] = useState("");
  const [balance, setBalance] = useState("");
  const [position, setPosition] = useState("");
  const [detail, setDetail] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState("roster");

  useEffect(() => {
    api.get("/admin/wwba/event", { params: { slug: EVENT_SLUG } })
      .then(({ data }) => setEventId(data.id))
      .catch(() => setEventErr("The WWBA event was not found in this environment. Create the 'wwba-florida' event first."));
  }, []);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    const params = { event_id: eventId };
    if (q) params.q = q;
    if (status) params.status = status;
    if (deposit) params.deposit = deposit;
    if (balance) params.balance = balance;
    if (position) params.position = position;
    try { const { data } = await api.get("/admin/wwba/registrations", { params }); setItems(data.items); }
    catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }, [eventId, q, status, deposit, balance, position]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (tab === "roster") load(); }, [tab, load]);

  const exportCsv = async () => {
    if (!eventId) return;
    try {
      const res = await api.get("/admin/wwba/registrations/export.csv", { params: { event_id: eventId }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "wwba_registrations.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-[1400px]" data-testid="admin-wwba">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Users className="w-4 h-4" /> CBR Nation Roster</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h1 className="font-display text-3xl uppercase tracking-tight">WWBA Florida — Registrations</h1><p className="text-xs text-slate-400 mt-1">Invite players, track deposits & roster readiness. Total $750 · Deposit $500 · Balance $250.</p></div>
          <div className="flex gap-2">
            <button data-testid="wwba-invite-btn" disabled={!eventId} onClick={() => setInviting(true)} className="inline-flex items-center gap-1 bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40"><Send className="w-4 h-4" /> Invite Player</button>
            <button data-testid="wwba-export" onClick={exportCsv} className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Download className="w-4 h-4" /> Export CSV</button>
          </div>
        </div>

        {eventErr && <div data-testid="wwba-event-error" className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-sm p-3">{eventErr}</div>}

        <div className="mt-5 flex gap-1 border-b border-slate-200">
          <TabBtn active={tab === "roster"} onClick={() => setTab("roster")} testid="wwba-tab-roster">Roster</TabBtn>
          <TabBtn active={tab === "balances"} onClick={() => setTab("balances")} testid="wwba-tab-balances">Final Balances</TabBtn>
        </div>

        {tab === "balances" ? <AdminWwbaBalances eventId={eventId} /> : (
        <>
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><input data-testid="wwba-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player or team…" className={`${inp} pl-8 w-full`} /></div>
          <select data-testid="wwba-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inp}><option value="">All statuses</option>{REG_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <select value={deposit} onChange={(e) => setDeposit(e.target.value)} className={inp}><option value="">Any deposit</option><option value="Deposit Paid">Deposit Paid</option><option value="Deposit Not Paid">Deposit Outstanding</option><option value="Deposit Pending">Deposit Pending</option></select>
          <select value={balance} onChange={(e) => setBalance(e.target.value)} className={inp}><option value="">Any balance</option><option value="outstanding">Balance Outstanding</option></select>
          <select value={position} onChange={(e) => setPosition(e.target.value)} className={inp}><option value="">Any position</option>{POS.map((p) => <option key={p}>{p}</option>)}</select>
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Player", "Pos", "Reg Status", "Deposit", "Paid", "Balance", "Shirt", "Jersey", "Hat", "J#1", "J#2", "Final #", "Team", "Date"].map((h) => <th key={h} className="px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={14} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={14} className="px-4 py-8 text-center text-slate-400">No registrations yet. Click "Invite Player".</td></tr>
                : items.map((r) => { const u = r.uniform || {}; return (
                  <tr key={r.id} data-testid={`wwba-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(r)}>
                    <td className="px-3 py-2 font-semibold text-[#0A192F] whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="px-3 py-2">{r.primary_position || "—"}{r.secondary_position ? `/${r.secondary_position}` : ""}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${REG_TONE[r.registration_status] || "bg-slate-100"}`}>{r.registration_status}</span></td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${DEP_TONE[r.deposit_status] || "bg-slate-100"}`}>{r.deposit_status}</span></td>
                    <td className="px-3 py-2 font-data">{money(r.total_paid_cents)}</td>
                    <td className="px-3 py-2 font-data text-slate-600">{money(r.balance_remaining_cents)}</td>
                    <td className="px-3 py-2">{u.shirt_size || "—"}</td>
                    <td className="px-3 py-2">{u.jersey_size || "—"}</td>
                    <td className="px-3 py-2">{u.hat_size || "—"}</td>
                    <td className="px-3 py-2">{u.jersey_choice_1 || "—"}</td>
                    <td className="px-3 py-2">{u.jersey_choice_2 || "—"}</td>
                    <td className="px-3 py-2 font-data font-semibold">{r.final_jersey_number || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.current_team || "—"}</td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{(r.completed_at || r.invited_at || "").slice(0, 10)}</td>
                  </tr>
                ); })}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      {detail && <DetailDrawer reg={detail} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); load(); }} />}
      {inviting && <InviteModal eventId={eventId} onClose={() => { setInviting(false); load(); }} onInvited={() => load()} />}
    </AdminLayout>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px ${active ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{children}</button>
);

function DetailDrawer({ reg, onClose, onSaved }) {
  const [jersey, setJersey] = useState(reg.final_jersey_number || "");
  const [status, setStatus] = useState(reg.registration_status);
  const a = reg.address || {}; const u = reg.uniform || {}; const p1 = reg.parent1 || {}; const p2 = reg.parent2 || {};
  const save = async () => {
    try { await api.patch(`/admin/wwba/registrations/${reg.id}`, { final_jersey_number: jersey, registration_status: status }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const Row = ({ l, v }) => <div className="flex justify-between py-1 border-b border-slate-100 text-sm"><span className="text-slate-400">{l}</span><span className="text-[#0A192F] font-medium text-right">{v || "—"}</span></div>;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div data-testid="wwba-detail" className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-display text-2xl uppercase">{reg.first_name} {reg.last_name}</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        <div className="flex gap-2 mt-2 text-xs">
          {reg.player?.slug && <a data-testid="wwba-detail-profile" href={`/prospects/${reg.player.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0A192F] underline"><ExternalLink className="w-3 h-3" /> Player Profile</a>}
          {reg.interest_id && <a href="/admin/interests" className="inline-flex items-center gap-1 text-[#0A192F] underline"><ExternalLink className="w-3 h-3" /> Interest Record</a>}
        </div>

        <Section t="Payment">
          <Row l="Total" v={money(reg.total_cost_cents)} /><Row l="Deposit" v={money(reg.deposit_amount_cents)} />
          <Row l="Paid" v={money(reg.total_paid_cents)} /><Row l="Balance" v={money(reg.balance_remaining_cents)} />
          <Row l="Deposit Status" v={reg.deposit_status} /><Row l="Paid At" v={(reg.deposit_paid_at || "").slice(0, 19).replace("T", " ")} />
        </Section>
        <Section t="Baseball">
          <Row l="Primary" v={reg.primary_position} /><Row l="Secondary" v={reg.secondary_position} />
          <Row l="Team" v={reg.current_team} /><Row l="High School" v={reg.high_school} />
          <Row l="Height" v={reg.height} /><Row l="Weight" v={reg.weight} /><Row l="DOB" v={reg.date_of_birth} /><Row l="Cell" v={reg.player_cell} />
        </Section>
        <Section t="Uniform">
          <Row l="Shirt" v={u.shirt_size} /><Row l="Jersey" v={u.jersey_size} /><Row l="Hat" v={u.hat_size} />
          <Row l="Jersey Choice #1" v={u.jersey_choice_1} /><Row l="Jersey Choice #2" v={u.jersey_choice_2} />
        </Section>
        <Section t="Address"><Row l="" v={[a.line1, a.line2, a.city, a.region, a.postal_code, a.country].filter(Boolean).join(", ")} /></Section>
        <Section t="Contacts">
          <Row l="Parent 1" v={[p1.name, p1.cell, p1.email].filter(Boolean).join(" · ")} />
          <Row l="Parent 2" v={[p2.name, p2.cell, p2.email].filter(Boolean).join(" · ")} />
        </Section>
        <Section t="Admin — Final Assignment">
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mt-1">Final Jersey Number</label>
          <input data-testid="wwba-final-jersey" value={jersey} onChange={(e) => setJersey(e.target.value)} className={`${inp} w-full mt-1`} />
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mt-3">Registration Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inp} w-full mt-1`}>{REG_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <button data-testid="wwba-detail-save" onClick={save} className="mt-4 w-full bg-[#E63946] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">Save</button>
        </Section>
      </div>
    </div>
  );
}
const Section = ({ t, children }) => <div className="mt-5"><div className="label-overline text-[10px] mb-2">{t}</div>{children}</div>;

function InviteModal({ eventId, onClose, onInvited }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [link, setLink] = useState(null);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get("/players", { params: { q, limit: 8 } }).then(({ data }) => setResults(data.items || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const invite = async (p) => {
    try { const { data } = await api.post("/admin/wwba/invite", { player_id: p.id, event_id: eventId, origin: window.location.origin }); setLink(data); toast.success(data.email_queued ? "Invite email queued (mock)" : "Invite created"); onInvited(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="wwba-invite-modal" className="bg-white rounded-sm w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-display text-xl uppercase">Invite Player to Register</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        {link ? (
          <div className="mt-4">
            <p className="text-sm text-emerald-600 font-semibold">Registration link created.</p>
            <div className="mt-2 flex gap-2">
              <input readOnly value={link.link} data-testid="wwba-invite-link" className={`${inp} flex-1 text-xs`} />
              <button onClick={() => { navigator.clipboard.writeText(link.link); toast.success("Copied"); }} className="inline-flex items-center gap-1 bg-[#0A192F] text-white px-3 rounded-sm text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Invite email {link.email_queued ? "was queued (mock in Preview)" : "not sent — no email on file"}.</p>
            <button onClick={onClose} className="mt-4 w-full border border-slate-300 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">Done</button>
          </div>
        ) : (
          <>
            <input data-testid="wwba-invite-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player by name…" className={`${inp} w-full mt-4`} />
            <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-slate-100">
              {results.map((p) => (
                <button key={p.id} data-testid={`wwba-invite-player-${p.id}`} onClick={() => invite(p)} className="w-full text-left py-2 px-1 hover:bg-slate-50 text-sm flex justify-between">
                  <span className="font-medium">{p.first_name} {p.last_name}</span><span className="text-slate-400 text-xs">{p.graduation_year} · {p.primary_position}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
